<template>
  <div>
    <div class="sticky top-[49px] z-1 gap-2.5 bg-primary p-4">
      <form
        class="flex w-full border"
        :class="errorMessage ? 'border-red' : 'border-white'"
        @submit.prevent="$emit('goToSearchPage', search)"
      >
        <div class="relative flex-auto">
          <input
            ref="inputEl"
            v-model="search"
            name="search"
            :placeholder="$t('search')"
            autocomplete="off"
            class="h-10 w-full pl-4 ui-text-xs text-white placeholder-black-40 focus:outline-none bg-transparent indent-0.5"
            @focus="handleFocus"
            @blur="handleBlur"
          />
        </div>
        <button
          type="button"
          class="inline-flex h-10 w-10 shrink-0 items-center justify-center"
          :class="isFocusedOrSearch && errorMessage ? 'text-red' : 'text-white'"
          :aria-label="isFocusedOrSearch ? $t('clearInput') : $t('search')"
          @click="handleClearSearchClick"
        >
          <UiIcon v-if="isFocusedOrSearch" name="close" class="size-5" />
          <UiIcon v-else name="search" class="size-6" />
        </button>
      </form>
      <SharedFieldHint v-if="errorMessage" :message="errorMessage" error />
    </div>
    <div v-if="search && !errorMessage" class="p-4">
      <h3
        v-if="listData.length"
        class="mt-0 ui-text-xs text-right text-white uppercase mb-4"
      >
        {{ `${listData.length} ${$t("results")}` }}
      </h3>
      <div
        v-if="loading && loadingPlaceholdersCount"
        class="divide-y divide-white"
      >
        <div
          v-for="i in loadingPlaceholdersCount"
          :key="i"
          class="flex w-full items-center gap-2.5 border-y-white py-5 first:border-t last:!border-b"
        >
          <UiIcon name="search" class="size-5 text-white" aria-hidden="true" />
          <div
            class="h-3.5 max-w-xl animate-pulse flex-auto rounded-2xl bg-black-40"
          />
        </div>
      </div>
      <ul v-if="listData.length && !loading" class="grid grid-cols-2 gap-y-8">
        <li v-for="product in listData" :key="product.id">
          <CommonProductCardSearch
            :product="product"
            @click="$emit('result-item-click')"
          />
        </li>
      </ul>
      <CommonAppHeaderSearchNoResults v-if="!listData.length && !loading" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { object, string } from "yup";
import { CatalogData } from "ecom-types";
const { $api } = useNuxtApp();
const { t } = useI18n();

const loading = ref(false);
const listData = ref<CatalogData[] | []>([]);
const inputEl = ref<HTMLInputElement>();
const isFocused = ref(false);
const loadingPlaceholdersCount = 3;
defineEmits(["result-item-click", "goToSearchPage"]);
let timeout: ReturnType<typeof setTimeout> | null = null;

const { searchEvent, dynamicParamsEvent } = useEvents();
const isMounted = useMounted();

const schema = object().shape({
  search: string().max(128, t("maximum128CharactersAllowed")),
});

const { handleSubmit } = useForm({
  validationSchema: schema,
});

const { value: search, errorMessage } = useField<string>("search");

const isFocusedOrSearch = computed(() => isFocused.value || search.value);

const requestParams = computed(() => ({
  search: search.value,
  count: 15,
}));

watch(requestParams, (val) => {
  if (timeout) {
    clearTimeout(timeout);
  }
  if (val && val.search) {
    loading.value = true;
    timeout = setTimeout(async () => {
      await getResult();
      searchEvent(val.search);
    }, 400);
  } else {
    listData.value = [];
  }
});

watchEffect(() => {
  if (isMounted.value && listData.value?.length) {
    dynamicParamsEvent(`View Search`, {
      ecomm_prodid: listData.value.map((item) => item.default_sku),
      ecomm_pagetype: "searchresults",
      ecomm_totalvalue: "",
      ecomm_category: "",
    });
  }
});

const getResult = handleSubmit(async () => {
  loading.value = true;
  try {
    const { data } = await $api.products.getCatalogNoRef(requestParams.value);
    listData.value = data;
  } catch (error) {
    listData.value = [];
    console.log("error - ", error);
  } finally {
    loading.value = false;
  }
});

function clearSearch() {
  search.value = "";
  if (inputEl.value) {
    inputEl.value.focus();
  }
}

function handleFocus() {
  isFocused.value = true;
}
function handleBlur() {
  isFocused.value = false;
}

function handleClearSearchClick() {
  if (isFocusedOrSearch.value) {
    clearSearch();
  }
}
</script>
